import React, { useState, useEffect } from "react";
import { PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined, EyeOutlined } from "@ant-design/icons";
import { Button as AntButton, Table as AntTable, Space as AntSpace, Select as AntSelect, Tag as AntTag, Input as AntInput, Row, Col, Modal } from "antd";
import { useNavigate } from "react-router-dom";
import { post } from "../../../../services/services";
import { ApiURL } from "../../../../services/apiConstants";
import { ErrorToast, SuccessToast } from "../../../../widgets/AppToast";

const { Option } = AntSelect;
const { confirm } = Modal;

const EquipmentRegisterList = () => {
    const navigate = useNavigate();
    const [loader, setLoader] = useState(false);
    const [dataSource, setDataSource] = useState([]);
    const [ships, setShips] = useState([]);
    const [selectedShipId, setSelectedShipId] = useState("");
    const [searchText, setSearchText] = useState("");

    const fetchShips = async () => {
        try {
            const FORM_DATA = new FormData();
            const res = await post(ApiURL.list_ships_view_com, FORM_DATA);
            if (res?.status === true) {
                setShips(res?.ships || []);
            }
        } catch (error) {
            console.error("Error fetching ships:", error);
        }
    };

    const fetchEquipment = async (shipId) => {
        setLoader(true);
        try {
            const FORM_DATA = new FormData();
            const user_Data = JSON.parse(localStorage.getItem("user_data"));
            FORM_DATA.append("company_id", user_Data?.data?.company?.company_id);
            if (shipId) {
                FORM_DATA.append("ship_id", shipId);
            }
            
            const res = await post(ApiURL.list_equipment, FORM_DATA);
            if (res?.status === true) {
                setDataSource(res?.data || res?.response_data || []);
            } else {
                setDataSource([]);
            }
        } catch (error) {
            console.error("Error fetching equipment:", error);
            setDataSource([]);
        } finally {
            setLoader(false);
        }
    };

    useEffect(() => {
        fetchShips();
        fetchEquipment("");
    }, []);

    const handleShipChange = (value) => {
        setSelectedShipId(value);
        fetchEquipment(value);
    };

    const handleDelete = (id) => {
        confirm({
            title: 'Are you sure you want to delete this equipment?',
            icon: <DeleteOutlined />,
            content: 'This action cannot be undone.',
            okText: 'Yes',
            okType: 'danger',
            cancelText: 'No',
            onOk: async () => {
                try {
                    const FORM_DATA = new FormData();
                    FORM_DATA.append("equipment_id", id);
                    FORM_DATA.append("id", id);
                    const res = await post(ApiURL.delete_equipment, FORM_DATA);
                    if (res?.status === true) {
                        SuccessToast("Success", "Equipment deleted successfully");
                        fetchEquipment(selectedShipId);
                    } else {
                        ErrorToast("Error", res?.message || "Failed to delete equipment");
                    }
                } catch (error) {
                    console.error("Delete error:", error);
                    ErrorToast("Error", "Something went wrong");
                }
            },
        });
    };

    const columns = [
        {
            title: "ID",
            key: "id",
            align: 'center',
            width: 80,
            render: (_, record) => record.id || record.equipment_id
        },
        {
            title: "Name",
            key: "name",
            align: 'center',
            render: (_, record) => record.name || "N/A"
        },
        {
            title: "Category",
            key: "category",
            align: 'center',
            render: (_, record) => {
                const cat = record.category;
                return <AntTag color="blue">{cat || "N/A"}</AntTag>;
            }
        },
        {
            title: "Manufacturer",
            key: "manufacturer",
            align: 'center',
            render: (_, record) => record.manufacturer || "N/A"
        },
        {
            title: "Model",
            key: "model",
            align: 'center',
            render: (_, record) => record.model || "N/A"
        },
        {
            title: "Location",
            key: "location_on_vessel",
            align: 'center',
            render: (_, record) => record.location_on_vessel || "N/A"
        },
        {
            title: "Status",
            dataIndex: "is_active",
            key: "is_active",
            align: 'center',
            render: (status) => {
                const isActive = status === true || status === 1 || status === "active" || status === "Active";
                return (
                    <AntTag color={isActive ? "green" : "red"}>
                        {isActive ? "Active" : "Inactive"}
                    </AntTag>
                );
            },
        },
        {
            title: "Action",
            key: "action",
            align: 'center',
            width: 150,
            render: (_, record) => (
                <AntSpace size="small" style={{ display: 'flex', justifyContent: 'center' }}>
                    <AntButton 
                        type="primary" 
                        size="small"
                        icon={<EyeOutlined style={{ fontSize: '16px' }} />} 
                        onClick={() => navigate(`/add-equipment`, { state: { editData: record, viewOnly: true } })}
                        style={{ 
                            backgroundColor: '#D91D3B', 
                            borderColor: '#D91D3B', 
                            width: '32px', 
                            height: '32px', 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'center',
                            borderRadius: '6px'
                        }}
                    />
                    <AntButton 
                        type="primary" 
                        size="small"
                        icon={<EditOutlined style={{ fontSize: '16px' }} />} 
                        onClick={() => navigate(`/add-equipment`, { state: { editData: record } })}
                        style={{ 
                            backgroundColor: '#D91D3B', 
                            borderColor: '#D91D3B', 
                            width: '32px', 
                            height: '32px', 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'center',
                            borderRadius: '6px'
                        }}
                    />
                    <AntButton 
                        type="primary"
                        danger
                        size="small"
                        icon={<DeleteOutlined style={{ fontSize: '16px' }} />} 
                        onClick={() => handleDelete(record.id || record.equipment_id)}
                        style={{ 
                            backgroundColor: '#D91D3B', 
                            borderColor: '#D91D3B', 
                            width: '32px', 
                            height: '32px', 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'center',
                            borderRadius: '6px'
                        }}
                    />
                </AntSpace>
            ),
        },
    ];

    return (
        <div className="white-container" style={{ padding: "10px" }}>
             <Row gutter={[16, 16]} style={{ marginBottom: '20px' }}>
                <Col xs={24} sm={8}>
                    <label className="block-label" style={{ fontWeight: '500', marginBottom: '8px', display: 'block' }}>Select Ship</label>
                    <AntSelect
                        placeholder="---- Select Ship ----"
                        style={{ width: '100%' }}
                        value={selectedShipId}
                        onChange={handleShipChange}
                        allowClear
                    >
                        <Option value="">All Ships</Option>
                        {ships.map(ship => (
                            <Option key={ship.id} value={ship.id}>
                                {ship.vessel_name}
                            </Option>
                        ))}
                    </AntSelect>
                </Col>
                <Col xs={24} sm={8}>
                    <label className="block-label" style={{ fontWeight: '500', marginBottom: '8px', display: 'block' }}>Search</label>
                    <AntInput 
                        placeholder="Search equipment..." 
                        prefix={<SearchOutlined />} 
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                    />
                </Col>
            </Row>

            <div className="row" style={{ padding: "15px 10px", marginTop: '10px' }}>
                <div className="col-6" style={{ alignSelf: "center" }}>
                    <div className="theme-content-title text-left">
                        <h3 style={{ margin: 0 }}>Equipment Register</h3>
                    </div>
                </div>
                <div className="col-6" style={{ display: "flex", justifyContent: "flex-end", alignItems: "center" }}>
                    <AntButton 
                        type="primary" 
                        icon={<PlusOutlined />} 
                        onClick={() => navigate("/add-equipment")}
                        className="save_btn"
                    >
                        Add Equipment
                    </AntButton>
                </div>
            </div>

            <AntTable 
                columns={columns} 
                dataSource={dataSource.filter(item => 
                    (item.name)?.toLowerCase().includes(searchText.toLowerCase()) ||
                    (item.id || item.equipment_id)?.toString().includes(searchText.toLowerCase())
                )} 
                loading={loader} 
                rowKey={(record) => record.id || record.equipment_id}
                bordered
                pagination={{ pageSize: 10 }}
                className="custom-table"
                style={{ marginTop: '10px' }}
            />
        </div>
    );
};

export default EquipmentRegisterList;
